import { useEffect, useState } from "react";
import { api, formatApiErrorDetail } from "../api";
import { PageHeader, Table, Badge, Btn, eur } from "../components/common";
import { useAuth } from "../auth";
import { toast } from "sonner";
import { Plus, X, ExternalLink, RefreshCw, RotateCw } from "lucide-react";
import { WebhookLogPanel } from "../components/WebhookLogPanel";
import { BillingPanel } from "../components/BillingPanel";
import { CatalogPlansTab } from "../components/CatalogPlansTab";
import { useAutoRefresh } from "../lib/autoRefresh";

const TABS = [
  { key: "tenants", label: "Tenants" },
  { key: "plans", label: "Planos" },
  { key: "billing", label: "Faturacao e add-ons" },
  { key: "webhook", label: "Webhook" },
  { key: "config", label: "Configuracao" },
];

export default function Privaxia() {
  const [tab, setTab] = useState("tenants");
  const [status, setStatus] = useState(null);
  const [platformId, setPlatformId] = useState(null);

  useEffect(() => {
    api.get("/privaxia/status").then((r) => setStatus(r.data)).catch(() => {});
    api.get("/platforms/lookup").then((r) => {
      const p = (r.data || []).find((x) => (x.slug || "").toLowerCase().includes("privaxia"));
      if (p) setPlatformId(p.id);
    }).catch(() => {});
  }, []);

  return (
    <div>
      <PageHeader overline="Portfolio - RGPD" title="Privaxia" />
      {status && !status.configured && (
        <div className="mb-6 px-4 py-3 rounded-lg bg-amber-50 text-amber-700 text-sm" data-testid="pv-not-configured">
          Integracao nao configurada. Va a Privaxia -> Configuracao e defina o URL e a chave de API.
        </div>
      )}
      <div className="flex gap-2 mb-8 border-b border-tint">
        {TABS.map((t) => (
          <button
            key={t.key}
            onClick={() => setTab(t.key)}
            data-testid={`pv-tab-${t.key}`}
            className={`px-4 py-2.5 text-sm font-semibold border-b-2 -mb-px transition-colors ${
              tab === t.key ? "border-blue text-blue" : "border-transparent text-muted2 hover:text-navy"
            }`}
          >
            {t.label}
          </button>
        ))}
      </div>
      {tab === "tenants" && <Tenants configured={status?.configured} platformId={platformId} />}
      {tab === "plans" && <CatalogPlansTab platformId={platformId} platformSlug="privaxia" />}
      {tab === "billing" && <BillingPanel baseUrl="/privaxia" supportsAddons={false} />}
      {tab === "webhook" && (
        <div className="space-y-6">
          <WebhookLogPanel origem="privaxia" title="Webhooks recebidos da Privaxia" />
          <WebhookPanel />
        </div>
      )}
      {tab === "config" && <Config status={status} onRefresh={() => api.get("/privaxia/status").then((r) => setStatus(r.data))} />}
    </div>
  );
}

function Tenants({ configured, platformId }) {
  const [rows, setRows] = useState([]);
  const [plans, setPlans] = useState([]);
  const [open, setOpen] = useState(false);
  const [planFor, setPlanFor] = useState(null);
  const [emailFor, setEmailFor] = useState(null);   // {kind:'admin', tenant, current}
  const [dpoFilter, setDpoFilter] = useState("");   // "" | "cogniseg" | "client" | "none"
  const [pwdFor, setPwdFor] = useState(null);       // {tenant, role, currentEmail}
  const [histFor, setHistFor] = useState(null);     // tenant
  const [detailsFor, setDetailsFor] = useState(null);
  const [metaFor, setMetaFor] = useState(null);
  const [syncState, setSyncState] = useState(null);

  const loadSyncState = () => api.get("/privaxia/sync-status").then((r) => setSyncState(r.data)).catch(() => {});
  const load = () => api.get("/privaxia/tenants").then((r) => setRows(r.data.tenants || [])).catch((e) => toast.error(formatApiErrorDetail(e.response?.data?.detail)));
  const loadPlans = () => {
    if (!platformId) return;
    api.get(`/plans?plataforma_id=${platformId}`).then((r) => setPlans(r.data || [])).catch(() => {});
  };
  useEffect(() => {
    if (!configured) return;
    load();
    loadSyncState();
    loadPlans();
    api.post("/privaxia/sync-subscriptions").then(loadSyncState).catch(() => {});
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [configured, platformId]);

  const syncSubs = async () => {
    try {
      const { data } = await api.post("/privaxia/sync-subscriptions");
      toast.success(`${data.synced} subscricoes sincronizadas`);
      loadSyncState();
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail));
    }
  };

  const act = async (slug, fn, label) => {
    try {
      await fn();
      toast.success(label);
      load();
    } catch (e) {
      const status = e.response?.status;
      const detail = formatApiErrorDetail(e.response?.data?.detail);
      // 404 upstream tipicamente = tenant apagado remotamente. Oferecer sincronizacao inline.
      if (status === 404) {
        toast.error(detail, {
          action: {
            label: "Sincronizar agora",
            onClick: () => syncSubs(),
          },
          duration: 8000,
        });
      } else {
        toast.error(detail);
      }
    }
  };

  const cols = [
    { key: "name", label: "Cliente", render: (r) => <span className="font-semibold text-navy">{r.name}</span> },
    { key: "slug", label: "Slug", render: (r) => <code className="text-xs bg-tint px-2 py-0.5 rounded">{r.slug}</code> },
    { key: "plan_key", label: "Plano", render: (r) => <Badge value={r.plan_key || r.plan || "-"} /> },
    { key: "dpo", label: "DPO", render: (r) => {
        if (r.dpo_mode === "cogniseg") {
          return <span className="inline-flex items-center gap-1 text-[10px] font-bold px-2 py-0.5 rounded bg-blue text-white" data-testid={`pv-dpo-badge-${r.slug}`} title="Servico DPO prestado pela Cogniseg">
            <span>DPO</span><span className="opacity-80">Cogniseg</span>
          </span>;
        }
        if (r.dpo_mode === "client") {
          return <span className="inline-flex items-center gap-1 text-[10px] font-bold px-2 py-0.5 rounded bg-amber-100 text-amber-800" data-testid={`pv-dpo-badge-${r.slug}`} title="DPO gerido pelo proprio cliente">
            <span>DPO</span><span className="opacity-80">Cliente</span>
          </span>;
        }
        return <span className="text-[10px] text-muted2 italic" title="Sem informacao - editar em Ficha -> Ajustar metadata local">n/d</span>;
      } },
    { key: "users_count", label: "Utiliz.", render: (r) => r.users_count ?? "-" },
    { key: "titulares_count", label: "Titulares", render: (r) => r.titulares_count ?? r.subjects_count ?? "-" },
    {
      key: "portal_url", label: "Portal",
      render: (r) => r.portal_url ? (
        <a href={r.portal_url} target="_blank" rel="noreferrer" className="text-blue hover:text-light-blue inline-flex items-center gap-1 text-xs">
          Abrir <ExternalLink size={12} />
        </a>
      ) : "-",
    },
    { key: "is_active", label: "Estado", render: (r) => <Badge value={r.is_active ? "ativa" : "suspensa"} /> },
    {
      key: "acoes", label: "Acoes",
      render: (r) => (
        <div className="flex gap-1 flex-wrap">
          {r.is_active ? (
            <button onClick={() => act(r.slug, () => api.delete(`/privaxia/tenants/${r.slug}`), "Tenant suspenso")} data-testid={`pv-suspend-${r.slug}`}
              className="text-xs px-2 py-1 rounded bg-red-50 text-red-600 hover:bg-red-100">Suspender</button>
          ) : (
            <button onClick={() => act(r.slug, () => api.post(`/privaxia/tenants/${r.slug}/reactivate`), "Tenant reativado")} data-testid={`pv-reactivate-${r.slug}`}
              className="text-xs px-2 py-1 rounded bg-tint text-navy hover:bg-blue hover:text-white">Reativar</button>
          )}
          <button onClick={() => setPlanFor(r)} data-testid={`pv-changeplan-${r.slug}`}
            className="text-xs px-2 py-1 rounded bg-tint text-navy hover:bg-blue hover:text-white">Mudar plano</button>
          <button onClick={() => setEmailFor({ kind: "admin", tenant: r, current: r.admin_email || "" })}
            data-testid={`pv-changeemail-${r.slug}`}
            className="text-xs px-2 py-1 rounded bg-tint text-navy hover:bg-blue hover:text-white">Alterar admin</button>
          <button onClick={() => setPwdFor({ tenant: r, role: "admin", currentEmail: r.admin_email || "" })}
            data-testid={`pv-password-${r.slug}`}
            className="text-xs px-2 py-1 rounded bg-tint text-navy hover:bg-blue hover:text-white">Password</button>
          <button onClick={() => setHistFor(r)}
            data-testid={`pv-history-${r.slug}`}
            className="text-xs px-2 py-1 rounded bg-tint text-navy hover:bg-blue hover:text-white">Historico</button>
          <button onClick={() => setDetailsFor(r)}
            data-testid={`pv-details-${r.slug}`}
            className="text-xs px-2 py-1 rounded bg-blue text-white hover:bg-navy font-semibold">Ficha</button>
        </div>
      ),
    },
  ];

  if (!configured) {
    return <div className="text-sm text-muted2" data-testid="pv-tenants-blocked">Configure primeiro as credenciais em "Configuracao".</div>;
  }

  return (
    <div>
      <div className="flex items-center justify-between mb-4 gap-2 flex-wrap">
        <div className="text-xs text-muted2" data-testid="pv-sync-state">
          {syncState?.last_run
            ? `Sincronizacao automatica diaria - ultima: ${new Date(syncState.last_run).toLocaleString("pt-PT", { dateStyle: "short", timeStyle: "short" })} (${syncState.last_count ?? 0} subscricoes, ${syncState.trigger || "-"})`
            : "Sincronizacao automatica diaria ativa"}
        </div>
        <div className="flex gap-2">
          <Btn variant="ghost" onClick={syncSubs} testid="pv-sync-subs"><RefreshCw size={16} /> Sincronizar subscricoes</Btn>
          <Btn variant="ghost" onClick={async () => {
            try {
              const res = await api.get(`/upstream-docs/privaxia/download`, { responseType: "blob" });
              const url = URL.createObjectURL(res.data);
              const a = document.createElement("a"); a.href = url;
              a.download = "PRIVAXIA_UPSTREAM_CONTRATOS.md";
              document.body.appendChild(a); a.click(); a.remove();
              setTimeout(() => URL.revokeObjectURL(url), 5000);
              toast.success("Contratos upstream descarregados (para colar no projeto Privaxia)");
            } catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail)); }
          }} testid="pv-download-upstream-md">Contratos upstream</Btn>
          <Btn variant="ghost" onClick={load} testid="pv-refresh-tenants"><RefreshCw size={16} /> Atualizar</Btn>
          <Btn onClick={() => setOpen(true)} testid="pv-new-tenant"><Plus size={16} /> Novo tenant</Btn>
        </div>
      </div>
      {rows.length > 0 && (() => {
        const counts = { cogniseg: 0, client: 0, none: 0 };
        rows.forEach((r) => {
          if (r.dpo_mode === "cogniseg") counts.cogniseg++;
          else if (r.dpo_mode === "client") counts.client++;
          else counts.none++;
        });
        const chips = [
          { key: "", label: "Todos", n: rows.length, tone: "bg-navy text-white", inactive: "bg-white text-navy border-slate-200" },
          { key: "cogniseg", label: "DPO Cogniseg", n: counts.cogniseg, tone: "bg-blue text-white", inactive: "bg-white text-blue border-blue/40" },
          { key: "client", label: "DPO Cliente", n: counts.client, tone: "bg-amber-500 text-white", inactive: "bg-white text-amber-700 border-amber-300" },
          { key: "none", label: "Sem info", n: counts.none, tone: "bg-slate-500 text-white", inactive: "bg-white text-slate-500 border-slate-300" },
        ];
        return (
          <div className="mb-3 flex items-center gap-2 flex-wrap" data-testid="pv-dpo-filter-chips">
            {chips.map((c) => (
              <button key={c.key || "all"} onClick={() => setDpoFilter(c.key)}
                data-testid={`pv-dpo-chip-${c.key || "all"}`}
                className={`text-xs font-semibold px-3 py-1.5 rounded-full border transition-colors ${dpoFilter === c.key ? c.tone + " border-transparent" : c.inactive + " hover:border-navy"}`}>
                {c.label} <span className="opacity-70">({c.n})</span>
              </button>
            ))}
          </div>
        );
      })()}
      <Table columns={cols} rows={rows.filter((r) => {
        if (!dpoFilter) return true;
        if (dpoFilter === "none") return !r.dpo_mode;
        return r.dpo_mode === dpoFilter;
      })} testid="pv-tenants-table" />
      {open && <TenantModal plans={plans} onClose={() => setOpen(false)} onSaved={() => { setOpen(false); load(); }} />}
      {planFor && <ChangePlanModal tenant={planFor} plans={plans} onClose={() => setPlanFor(null)} onSaved={() => { setPlanFor(null); load(); }} onSync={syncSubs} />}
      {emailFor && <ChangeEmailModal state={emailFor} platform="privaxia" onClose={() => setEmailFor(null)} onSaved={() => { setEmailFor(null); load(); }} onSync={syncSubs} />}
      {pwdFor && <PasswordActionsModal state={pwdFor} platform="privaxia" onClose={() => setPwdFor(null)} onSaved={() => setPwdFor(null)} onSync={syncSubs} />}
      {histFor && <TenantHistoryModal tenant={histFor} entidade="privaxia_tenant" onClose={() => setHistFor(null)} />}
      {detailsFor && (
        <TenantDetailsModal
          tenant={detailsFor}
          entidade="privaxia_tenant"
          platform="privaxia"
          onClose={() => setDetailsFor(null)}
          onAction={(kind) => {
            const t = detailsFor;
            setDetailsFor(null);
            if (kind === "change-email-admin") setEmailFor({ kind: "admin", tenant: t, current: t.admin_email || "" });
            else if (kind === "password-admin") setPwdFor({ tenant: t, role: "admin", currentEmail: t.admin_email || "" });
            else if (kind === "local-metadata") setMetaFor(t);
          }}
        />
      )}
      {metaFor && (
        <LocalMetadataModal tenant={metaFor} platform="privaxia"
          onClose={() => setMetaFor(null)}
          onSaved={() => { setMetaFor(null); load(); }} />
      )}
    </div>
  );
}

function TenantModal({ plans, onClose, onSaved }) {
  const firstPlan = plans[0] || {};
  const [form, setForm] = useState({
    name: "", slug: "",
    plan_key: firstPlan.upstream_plan_key || firstPlan.sku || "",
    plano_id: firstPlan.id || "",
    admin_name: "", admin_email: "", admin_password: "",
    nif: "", billing_email: "",
    periodo: (firstPlan.periodos_disponiveis || ["anual"])[0],
    account_id: "",
  });
  const [slugState, setSlugState] = useState(null);
  const [created, setCreated] = useState(null);
  const [saving, setSaving] = useState(false);
  const [accounts, setAccounts] = useState([]);
  const [accountSearch, setAccountSearch] = useState("");
  const [showAccountPicker, setShowAccountPicker] = useState(false);
  const upd = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  useEffect(() => {
    api.get("/crm/accounts").then(({ data }) => setAccounts(data || [])).catch(() => {});
  }, []);

  const filteredAccounts = accounts.filter((a) =>
    !accountSearch || a.nome?.toLowerCase().includes(accountSearch.toLowerCase()) || String(a.nif || '').includes(accountSearch)
  ).slice(0, 12);

  const selectAccount = (acc) => {
    setForm((f) => ({ ...f, name: acc.nome || f.name, nif: acc.nif || f.nif, admin_name: acc.nome || f.admin_name, admin_email: acc.email || f.admin_email, billing_email: acc.contacto_faturacao_email || acc.email || f.billing_email, account_id: acc.id }));
    setAccountSearch(acc.nome || "");
    setShowAccountPicker(false);
  };

  const selectedPlan = plans.find((p) => p.id === form.plano_id) || {};
  const MENSAL_KEY = { anual: "preco_mensal_1ano", "3anos": "preco_mensal_3anos", "5anos": "preco_mensal_5anos" };
  const previewMrr = parseFloat(selectedPlan[MENSAL_KEY[form.periodo]] || 0);

  const selectPlan = (planId) => {
    const p = plans.find((x) => x.id === planId) || {};
    setForm((f) => ({ ...f, plano_id: planId, plan_key: p.upstream_plan_key || p.sku || "", periodo: (p.periodos_disponiveis || ["anual"])[0] }));
  };

  const passwordOk = (form.admin_password || "").length >= 8;
  const canCreate = form.name && form.slug && form.plan_key && form.admin_name && form.admin_email && passwordOk;

  const checkSlug = async () => {
    if (!form.slug) return;
    try {
      const { data } = await api.get(`/privaxia/tenants/slug-check/${form.slug}`);
      setSlugState(data.available ?? data.is_available ?? null);
    } catch (e) {
      setSlugState(null);
    }
  };

  const save = async () => {
    if (!canCreate) return toast.error("Preencha todos os campos obrigatorios (password min 8 caracteres)");
    setSaving(true);
    try {
      const { data } = await api.post("/privaxia/tenants", form);
      setCreated(data);
      if (data.upstream_ok) toast.success("Tenant criado e provisionado na plataforma");
      else toast.success("Tenant criado localmente (provisioning upstream pendente)");
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail));
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="fixed inset-0 bg-black/40 flex items-center justify-center z-50 p-4" data-testid="pv-tenant-modal">
      <div className="bg-white rounded-xl p-8 w-full max-w-md max-h-[90vh] overflow-y-auto">
        <div className="flex justify-between items-center mb-6">
          <h2 className="text-navy text-xl font-bold tracking-tight">Novo tenant Privaxia</h2>
          <button onClick={onClose}><X className="text-muted2" /></button>
        </div>
        {!created ? (
          <>
            {/* Pesquisa de cliente existente */}
            <div className="mb-4 relative">
              <label className="block text-xs font-semibold text-navy mb-1">Selecionar cliente existente (opcional)</label>
              <div className="relative">
                <input value={accountSearch} onChange={(e) => { setAccountSearch(e.target.value); setShowAccountPicker(true); }}
                  onFocus={() => setShowAccountPicker(true)}
                  placeholder="Pesquisar por nome ou NIF..." data-testid="pv-account-search"
                  className="w-full px-3 py-2 border border-tint rounded-lg text-sm pr-8 focus:border-blue focus:outline-none" />
                {accountSearch && <button onClick={() => { setAccountSearch(""); setShowAccountPicker(false); upd("account_id", ""); }} className="absolute right-2 top-2.5 text-slate-400"><X size={14} /></button>}
              </div>
              {showAccountPicker && accountSearch && (
                <div className="absolute z-10 w-full bg-white border border-tint rounded-lg shadow-lg mt-1 max-h-48 overflow-y-auto">
                  {filteredAccounts.length === 0 && <div className="px-3 py-2 text-xs text-slate-400">Nenhum cliente encontrado</div>}
                  {filteredAccounts.map((a) => (
                    <button key={a.id} onClick={() => selectAccount(a)} data-testid={`pv-account-option-${a.id}`}
                      className="w-full text-left px-3 py-2 hover:bg-blue/5 text-sm">
                      <span className="font-semibold text-navy">{a.nome}</span>
                      {a.nif && <span className="ml-2 text-xs text-slate-400">NIF: {a.nif}</span>}
                      {a.email && <span className="ml-2 text-xs text-slate-400">{a.email}</span>}
                    </button>
                  ))}
                </div>
              )}
              {form.account_id && <div className="text-[10px] text-green-600 mt-0.5">Cliente CRM vinculado</div>}
            </div>
            <div className="border-t border-tint pt-3 mb-3"><span className="text-[10px] text-slate-400 uppercase tracking-wider">Dados do tenant</span></div>
            <label className="block text-sm font-semibold text-navy mb-1">Nome do cliente</label>
            <input value={form.name} onChange={(e) => upd("name", e.target.value)} data-testid="pv-tenant-name" className="w-full mb-3 px-3 py-2 border border-tint rounded-lg" />
            <label className="block text-sm font-semibold text-navy mb-1">Slug (URL-friendly)</label>
            <div className="flex gap-2 mb-1">
              <input value={form.slug} onChange={(e) => upd("slug", e.target.value.toLowerCase().replace(/[^a-z0-9-]/g, ""))} onBlur={checkSlug} data-testid="pv-tenant-slug" className="flex-1 px-3 py-2 border border-tint rounded-lg" />
              <Btn variant="ghost" onClick={checkSlug} testid="pv-slug-check">Verificar</Btn>
            </div>
            {slugState === true && <div className="text-xs text-green-600 mb-3">Slug disponivel</div>}
            {slugState === false && <div className="text-xs text-red-600 mb-3">Slug ja em uso</div>}
            {slugState === null && <div className="mb-3" />}
            <label className="block text-sm font-semibold text-navy mb-1">Plano</label>
            <select value={form.plano_id} onChange={(e) => selectPlan(e.target.value)} data-testid="pv-tenant-plan" className="w-full mb-2 px-3 py-2 border border-tint rounded-lg">
              {plans.map((p) => <option key={p.id} value={p.id}>{p.nome} ({p.sku})</option>)}
            </select>
            {selectedPlan.id && (
              <div className="mb-3 p-2.5 rounded-lg bg-tint text-xs space-y-1" data-testid="pv-plan-preview">
                <div className="flex justify-between"><span className="text-muted2">Max utilizadores</span><span className="font-semibold text-navy">{selectedPlan.max_utilizadores || "Ilimitado"}</span></div>
                {(selectedPlan.max_titulares ?? -1) >= 0 && <div className="flex justify-between"><span className="text-muted2">Max titulares</span><span className="font-semibold text-navy">{selectedPlan.max_titulares}</span></div>}
                <div className="flex justify-between"><span className="text-muted2">MRR estimado</span><span className="font-bold text-blue">{previewMrr.toFixed(2)} EUR/mes</span></div>
              </div>
            )}

            <div className="border-t border-tint pt-4 mb-4">
              <div className="text-xs uppercase tracking-[0.15em] text-blue font-semibold mb-3">Administrador inicial</div>
              <label className="block text-sm font-semibold text-navy mb-1">Nome</label>
              <input value={form.admin_name} onChange={(e) => upd("admin_name", e.target.value)} data-testid="pv-tenant-admin-name" className="w-full mb-3 px-3 py-2 border border-tint rounded-lg" />
              <label className="block text-sm font-semibold text-navy mb-1">Email</label>
              <input value={form.admin_email} onChange={(e) => upd("admin_email", e.target.value)} data-testid="pv-tenant-admin-email" className="w-full mb-3 px-3 py-2 border border-tint rounded-lg" />
              <label className="block text-sm font-semibold text-navy mb-1">Password (min 8 caracteres)</label>
              <input type="password" value={form.admin_password} onChange={(e) => upd("admin_password", e.target.value)} data-testid="pv-tenant-admin-password" className="w-full px-3 py-2 border border-tint rounded-lg" />
              {form.admin_password && !passwordOk && <div className="text-xs text-red-600 mt-1">Minimo 8 caracteres.</div>}
            </div>

            <div className="border-t border-tint pt-4 mb-4">
              <div className="text-xs uppercase tracking-[0.15em] text-blue font-semibold mb-3">Dados para gestao da subscricao</div>
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <label className="block text-sm font-semibold text-navy mb-1">NIF</label>
                  <input value={form.nif} onChange={(e) => upd("nif", e.target.value)} data-testid="pv-tenant-nif" className="w-full px-3 py-2 border border-tint rounded-lg" />
                </div>
                <div>
                  <label className="block text-sm font-semibold text-navy mb-1">Periodo</label>
                  <select value={form.periodo} onChange={(e) => upd("periodo", e.target.value)} data-testid="pv-tenant-periodo" className="w-full px-3 py-2 border border-tint rounded-lg">
                    {(selectedPlan.periodos_disponiveis || ["anual"]).map((p) => (
                      <option key={p} value={p}>{p === "anual" ? "Anual (1 ano)" : p === "3anos" ? "3 anos" : p === "5anos" ? "5 anos" : p}</option>
                    ))}
                  </select>
                </div>
              </div>
              <label className="block text-sm font-semibold text-navy mb-1 mt-3">Email de faturacao</label>
              <input value={form.billing_email} onChange={(e) => upd("billing_email", e.target.value)} data-testid="pv-tenant-billing-email" className="w-full px-3 py-2 border border-tint rounded-lg" placeholder="Se vazio, usa o email do admin" />
              <p className="text-xs text-muted2 mt-2">O plano define o MRR da subscricao. A subscricao fica ativa e visivel no Dashboard.</p>
            </div>

            <Btn onClick={save} disabled={!canCreate || saving} testid="pv-tenant-save">{saving ? "A criar..." : "Criar tenant"}</Btn>
          </>
        ) : (
          <div>
            <div className="bg-green-50 text-green-700 rounded-lg p-4 text-sm mb-4">Tenant Privaxia provisionado com sucesso.</div>
            <div className="space-y-2 text-sm mb-6">
              {created.portal_url && <div><span className="text-muted2">Portal:</span> <a className="text-blue" href={created.portal_url} target="_blank" rel="noreferrer">{created.portal_url}</a></div>}
              {created.admin_login_url && <div><span className="text-muted2">Login admin:</span> <a className="text-blue" href={created.admin_login_url} target="_blank" rel="noreferrer">{created.admin_login_url}</a></div>}
              <div className="bg-tint rounded-lg p-3">
                <div className="text-muted2 text-xs mb-1">Credenciais do admin:</div>
                <div><b>{form.admin_email}</b></div>
                <div className="text-xs">A password inicial foi enviada no pedido. Guarde-a em local seguro.</div>
              </div>
            </div>
            <Btn onClick={onSaved} testid="pv-tenant-done">Concluir</Btn>
          </div>
        )}
      </div>
    </div>
  );
}

function ChangePlanModal({ tenant, plans, onClose, onSaved, onSync }) {
  const currentPlan = plans.find((p) => (p.upstream_plan_key || p.sku) === (tenant.plan_key || tenant.plan) || p.nome === (tenant.plan_key || tenant.plan));
  const [planoId, setPlanoId] = useState(currentPlan?.id || plans[0]?.id || "");

  const selected = plans.find((p) => p.id === planoId) || {};
  const MENSAL_KEY = { anual: "preco_mensal_1ano", "3anos": "preco_mensal_3anos", "5anos": "preco_mensal_5anos" };

  const save = async () => {
    const plan_key = selected.upstream_plan_key || selected.sku || "";
    try {
      const { data } = await api.post(`/privaxia/tenants/${tenant.slug}/change-plan`, { plan_key, plano_id: planoId });
      if (data.upstream_ok) toast.success("Plano alterado e sincronizado");
      else toast.success("Plano alterado localmente (upstream pendente)");
      onSaved();
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail));
    }
  };

  return (
    <div className="fixed inset-0 bg-black/40 flex items-center justify-center z-50 p-4" data-testid="pv-changeplan-modal">
      <div className="bg-white rounded-xl p-8 w-full max-w-md">
        <div className="flex justify-between items-center mb-6">
          <h2 className="text-navy text-xl font-bold tracking-tight">Mudar plano - {tenant.name}</h2>
          <button onClick={onClose}><X className="text-muted2" /></button>
        </div>
        <label className="block text-sm font-semibold text-navy mb-1">Plano</label>
        <select value={planoId} onChange={(e) => setPlanoId(e.target.value)} data-testid="pv-changeplan-select" className="w-full mb-2 px-3 py-2 border border-tint rounded-lg">
          {plans.map((p) => <option key={p.id} value={p.id}>{p.nome} ({p.sku})</option>)}
        </select>
        {selected.id && (
          <div className="mb-4 p-2.5 rounded-lg bg-tint text-xs space-y-1">
            <div className="flex justify-between"><span className="text-muted2">Max utilizadores</span><span className="font-semibold text-navy">{selected.max_utilizadores || "Ilimitado"}</span></div>
            {(selected.max_titulares ?? -1) >= 0 && <div className="flex justify-between"><span className="text-muted2">Max titulares</span><span className="font-semibold text-navy">{selected.max_titulares}</span></div>}
            {(selected.periodos_disponiveis || []).map((per) => (
              <div key={per} className="flex justify-between"><span className="text-muted2">MRR {per === "anual" ? "1 ano" : per}</span><span className="font-semibold text-navy">{(selected[MENSAL_KEY[per]] || 0).toFixed(2)} EUR</span></div>
            ))}
          </div>
        )}
        <Btn onClick={save} testid="pv-changeplan-save">Aplicar</Btn>
      </div>
    </div>
  );
};

function Plans({ configured }) {
  const [rows, setRows] = useState([]);
  const [edit, setEdit] = useState(null);

  const load = () => api.get("/privaxia/plans").then((r) => setRows(r.data.plans || [])).catch((e) => toast.error(formatApiErrorDetail(e.response?.data?.detail)));
  useEffect(() => { if (configured) load(); }, [configured]);
  useAutoRefresh(load);

  const cols = [
    { key: "name", label: "Plano", render: (r) => <span className="font-semibold text-navy">{r.name}</span> },
    { key: "key", label: "Chave", render: (r) => <code className="text-xs bg-tint px-2 py-0.5 rounded">{r.key}</code> },
    { key: "max_users", label: "Max utiliz.", render: (r) => (r.max_users === -1 ? "ilimitado" : r.max_users) },
    { key: "max_titulares", label: "Max titulares", render: (r) => (r.max_titulares === -1 ? "ilimitado" : (r.max_titulares ?? "-")) },
    { key: "max_tratamentos", label: "Max tratamentos", render: (r) => (r.max_tratamentos === -1 ? "ilimitado" : (r.max_tratamentos ?? "-")) },
    { key: "price_monthly", label: "Preco/mes", render: (r) => eur(r.price_monthly) },
    { key: "acoes", label: "Acoes", render: (r) => <button onClick={() => setEdit(r)} data-testid={`pv-editplan-${r.key}`} className="text-xs px-2 py-1 rounded bg-tint text-navy hover:bg-blue hover:text-white">Editar</button> },
  ];

  if (!configured) return <div className="text-sm text-muted2">Configure primeiro as credenciais em "Configuracao".</div>;

  return (
    <div>
      <div className="flex justify-end mb-4 gap-2">
        <Btn variant="ghost" onClick={load}><RefreshCw size={16} /> Atualizar</Btn>
        <Btn onClick={() => setEdit({ _new: true, key: "", name: "", max_users: 5, max_titulares: 1000, max_tratamentos: 50, price_monthly: 0, description: "" })} testid="pv-new-plan"><Plus size={16} /> Novo plano</Btn>
      </div>
      <Table columns={cols} rows={rows} testid="pv-plans-table" />
      {edit && <PlanModal plan={edit} onClose={() => setEdit(null)} onSaved={() => { setEdit(null); load(); }} />}
    </div>
  );
}

function PlanModal({ plan, onClose, onSaved }) {
  const isNew = plan._new;
  const [form, setForm] = useState({
    key: plan.key || "", name: plan.name || "", description: plan.description || "",
    max_users: plan.max_users ?? 5, max_titulares: plan.max_titulares ?? 1000,
    max_tratamentos: plan.max_tratamentos ?? 50,
    price_monthly: plan.price_monthly ?? 0,
  });
  const upd = (k, v) => setForm({ ...form, [k]: v });

  const save = async () => {
    const body = {
      ...form,
      max_users: Number(form.max_users), max_titulares: Number(form.max_titulares),
      max_tratamentos: Number(form.max_tratamentos),
      price_monthly: Number(form.price_monthly),
    };
    try {
      if (isNew) await api.post("/privaxia/plans", body);
      else await api.put(`/privaxia/plans/${plan.key}`, body);
      toast.success("Plano guardado");
      onSaved();
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail));
    }
  };

  const del = async () => {
    if (!confirm("Eliminar este plano? So e possivel se nao estiver em uso.")) return;
    try {
      await api.delete(`/privaxia/plans/${plan.key}`);
      toast.success("Plano eliminado");
      onSaved();
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail));
    }
  };

  return (
    <div className="fixed inset-0 bg-black/40 flex items-center justify-center z-50 p-4" data-testid="pv-plan-modal">
      <div className="bg-white rounded-xl p-8 w-full max-w-md max-h-[90vh] overflow-y-auto">
        <div className="flex justify-between items-center mb-6">
          <h2 className="text-navy text-xl font-bold tracking-tight">{isNew ? "Novo plano" : `Editar ${form.name}`}</h2>
          <button onClick={onClose}><X className="text-muted2" /></button>
        </div>
        <label className="block text-sm font-semibold text-navy mb-1">Chave (key)</label>
        <input value={form.key} disabled={!isNew} onChange={(e) => upd("key", e.target.value.toLowerCase().replace(/[^a-z0-9-]/g, ""))} data-testid="pv-plan-key" className="w-full mb-3 px-3 py-2 border border-tint rounded-lg disabled:bg-rowalt" />
        <label className="block text-sm font-semibold text-navy mb-1">Nome</label>
        <input value={form.name} onChange={(e) => upd("name", e.target.value)} data-testid="pv-plan-name" className="w-full mb-3 px-3 py-2 border border-tint rounded-lg" />
        <label className="block text-sm font-semibold text-navy mb-1">Descricao</label>
        <input value={form.description} onChange={(e) => upd("description", e.target.value)} className="w-full mb-3 px-3 py-2 border border-tint rounded-lg" />
        <div className="grid grid-cols-2 gap-3">
          <div>
            <label className="block text-sm font-semibold text-navy mb-1">Max utilizadores (-1=ilim.)</label>
            <input type="number" value={form.max_users} onChange={(e) => upd("max_users", e.target.value)} className="w-full mb-3 px-3 py-2 border border-tint rounded-lg" />
          </div>
          <div>
            <label className="block text-sm font-semibold text-navy mb-1">Max titulares (-1=ilim.)</label>
            <input type="number" value={form.max_titulares} onChange={(e) => upd("max_titulares", e.target.value)} className="w-full mb-3 px-3 py-2 border border-tint rounded-lg" />
          </div>
          <div>
            <label className="block text-sm font-semibold text-navy mb-1">Max tratamentos (-1=ilim.)</label>
            <input type="number" value={form.max_tratamentos} onChange={(e) => upd("max_tratamentos", e.target.value)} data-testid="pv-plan-max-tratamentos" className="w-full mb-3 px-3 py-2 border border-tint rounded-lg" />
          </div>
          <div>
            <label className="block text-sm font-semibold text-navy mb-1">Preco/mes (EUR)</label>
            <input type="number" value={form.price_monthly} onChange={(e) => upd("price_monthly", e.target.value)} className="w-full mb-3 px-3 py-2 border border-tint rounded-lg" />
          </div>
        </div>
        <div className="flex gap-2 mt-2">
          <Btn onClick={save} testid="pv-plan-save">Guardar</Btn>
          {!isNew && <Btn variant="danger" onClick={del} testid="pv-plan-delete">Eliminar</Btn>}
        </div>
      </div>
    </div>
  );
}

function WebhookPanel() {
  const [logs, setLogs] = useState([]);
  const [testing, setTesting] = useState(false);

  const load = () => api.get("/privaxia/webhook/logs").then((r) => setLogs(r.data.logs || r.data.entries || [])).catch(() => {});
  useEffect(() => { load(); }, []);

  const sendTest = async () => {
    setTesting(true);
    try {
      await api.post("/privaxia/webhook/test");
      toast.success("Evento de teste enviado");
      setTimeout(load, 1500);
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail));
    } finally {
      setTesting(false);
    }
  };

  return (
    <div>
      <div className="flex justify-between items-center mb-4">
        <div className="text-sm text-muted2">Ultimas entregas de webhook enviadas pela Privaxia</div>
        <div className="flex gap-2">
          <Btn variant="ghost" onClick={load} testid="pv-webhook-refresh"><RefreshCw size={16} /> Atualizar</Btn>
          <Btn onClick={sendTest} disabled={testing} testid="pv-webhook-test">{testing ? "A enviar..." : "Enviar teste"}</Btn>
        </div>
      </div>
      {logs.length === 0 ? (
        <div className="text-sm text-muted2 border border-tint rounded-lg p-6 text-center" data-testid="pv-webhook-empty">Nenhuma entrega registada.</div>
      ) : (
        <div className="space-y-2" data-testid="pv-webhook-logs">
          {logs.map((l, i) => (
            <div key={i} className="border border-tint rounded-lg p-3 text-sm bg-white">
              <div className="flex justify-between items-center mb-1">
                <span className="font-semibold text-navy">{l.event || l.type || "?"}</span>
                <Badge value={l.status || (l.success ? "ok" : "falhou")} />
              </div>
              <div className="text-xs text-muted2">{l.timestamp || l.created_at || l.at || "-"}</div>
              {l.response_code && <div className="text-xs">HTTP {l.response_code}</div>}
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

function Config({ status, onRefresh }) {
  const [config, setConfig] = useState(null);
  const [webhookUrl, setWebhookUrl] = useState("");
  const [webhookSecret, setWebhookSecret] = useState("");
  const [settings, setSettings] = useState(null);
  const [apiUrl, setApiUrl] = useState("");
  const [apiKey, setApiKey] = useState("");
  const [manualSecret, setManualSecret] = useState("");
  const [rotating, setRotating] = useState(false);

  const loadSettings = () => api.get("/privaxia/settings").then((r) => {
    setSettings(r.data);
    setApiUrl(r.data.api_url || "");
  }).catch(() => {});

  useEffect(() => {
    loadSettings();
    if (status?.configured) {
      api.get("/privaxia/config").then((r) => setConfig(r.data)).catch(() => {});
    }
    const backend = process.env.REACT_APP_BACKEND_URL;
    setWebhookUrl(`${backend}/api/webhooks/privaxia`);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [status?.configured]);

  const saveCredentials = async () => {
    try {
      const body = { api_url: apiUrl };
      if (apiKey.trim()) body.api_key = apiKey.trim();
      if (manualSecret.trim()) body.webhook_secret = manualSecret.trim();
      await api.put("/privaxia/settings", body);
      setApiKey("");
      setManualSecret("");
      toast.success("Credenciais guardadas (cifradas)");
      loadSettings();
      onRefresh?.();
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail));
    }
  };

  const testConnection = async () => {
    try {
      const { data } = await api.post("/privaxia/settings/test");
      toast.success(`Ligacao OK - ${data.health?.platform || data.health?.service || "Privaxia"}`);
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail));
    }
  };

  const registerWebhook = async () => {
    try {
      const { data } = await api.put("/privaxia/webhook", { url: webhookUrl });
      setWebhookSecret(data.signing_secret || "");
      toast.success("Webhook registado - signing secret guardado cifrado");
      loadSettings();
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail));
    }
  };

  const rotateKey = async () => {
    if (!confirm("Rodar a chave de API remotamente? A chave anterior deixa de funcionar imediatamente.")) return;
    setRotating(true);
    try {
      const { data } = await api.post("/privaxia/rotate-key");
      if (data.rotated) toast.success("Chave rodada e guardada cifrada");
      else toast.error("A Privaxia nao devolveu a nova chave");
      loadSettings();
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail));
    } finally {
      setRotating(false);
    }
  };

  return (
    <div className="max-w-2xl space-y-8">
      <div className="border border-tint rounded-lg p-6" data-testid="pv-credentials-card">
        <h3 className="text-navy font-bold text-lg tracking-tight mb-1">Ligacao a Privaxia</h3>
        <p className="text-muted2 text-sm mb-4">
          Estas credenciais sao usadas apenas pelo backend do SaaS-Hub - a chave nunca vai ao browser e e guardada cifrada na base de dados.
        </p>
        <label className="block text-sm font-semibold text-navy mb-1">URL da API</label>
        <input value={apiUrl} onChange={(e) => setApiUrl(e.target.value)} data-testid="pv-api-url" className="w-full mb-3 px-3 py-2 border border-tint rounded-lg" placeholder="https://privaxia.cogniseg.net" />
        <label className="block text-sm font-semibold text-navy mb-1">Chave de API (X-API-Key)</label>
        <input type="password" value={apiKey} onChange={(e) => setApiKey(e.target.value)} data-testid="pv-api-key" className="w-full mb-1 px-3 py-2 border border-tint rounded-lg font-mono" placeholder={settings?.api_key_set ? "Definida - deixe vazio para manter" : "Cole a nova chave"} />
        <div className="text-xs text-muted2 mb-4">
          {settings?.api_key_set
            ? `Atual: ${settings.api_key_masked} (origem: ${settings.api_key_source === "db" ? "definicoes" : "ambiente"})`
            : "Nenhuma chave definida."}
        </div>
        <div className="flex gap-2 flex-wrap">
          <Btn onClick={saveCredentials} testid="pv-save-credentials">Guardar credenciais</Btn>
          <Btn variant="ghost" onClick={testConnection} testid="pv-test-connection"><RefreshCw size={16} /> Testar ligacao</Btn>
          {settings?.api_key_set && (
            <Btn variant="ghost" onClick={rotateKey} disabled={rotating} testid="pv-rotate-key"><RotateCw size={16} /> {rotating ? "A rodar..." : "Rodar chave"}</Btn>
          )}
        </div>
        <p className="text-xs text-muted2 mt-3">
          Ao rodar a chave (aqui ou na Privaxia): a nova chave e guardada automaticamente. Nao e preciso reiniciar nada.
        </p>
      </div>

      {config && (
        <div className="border border-tint rounded-lg p-6" data-testid="pv-info-card">
          <h3 className="text-navy font-bold text-lg tracking-tight mb-1">Informacao da plataforma</h3>
          <div className="text-sm space-y-1 text-body mt-3">
            {config.platform_name && <div className="flex justify-between"><span className="text-muted2">Nome</span><b>{config.platform_name}</b></div>}
            {config.version && <div className="flex justify-between"><span className="text-muted2">Versao</span><b>{config.version}</b></div>}
            {config.public_base_url && <div className="flex justify-between"><span className="text-muted2">URL publico</span><a className="text-blue" href={config.public_base_url} target="_blank" rel="noreferrer">{config.public_base_url}</a></div>}
            {Array.isArray(config.features) && config.features.length > 0 && (
              <div className="pt-2">
                <div className="text-muted2 mb-1">Features</div>
                <div className="flex gap-1 flex-wrap">{config.features.map((f) => <Badge key={f} value={f} />)}</div>
              </div>
            )}
          </div>
        </div>
      )}

      <div className="border border-tint rounded-lg p-6">
        <h3 className="text-navy font-bold text-lg tracking-tight mb-1">Webhook de estado (signing secret)</h3>
        <p className="text-muted2 text-sm mb-4">Recebe eventos da Privaxia (tenant criado, suspenso, plano alterado), validados por HMAC. O signing secret e guardado cifrado.</p>
        <label className="block text-sm font-semibold text-navy mb-1">URL do recetor</label>
        <input value={webhookUrl} onChange={(e) => setWebhookUrl(e.target.value)} data-testid="pv-webhook-url" className="w-full mb-3 px-3 py-2 border border-tint rounded-lg text-sm" />
        <div className="text-xs text-muted2 mb-3">
          {settings?.webhook_secret_set ? `Signing secret guardado: ${settings.webhook_secret_masked}` : "Sem signing secret guardado."}
        </div>
        <Btn onClick={registerWebhook} testid="pv-webhook-register">Registar webhook e obter secret</Btn>
        {webhookSecret && (
          <div className="mt-3 bg-tint rounded-lg p-3 text-xs">
            <div className="text-muted2 mb-1">signing_secret (guardado cifrado no backend):</div>
            <code className="break-all">{webhookSecret}</code>
          </div>
        )}
        <div className="mt-5 pt-4 border-t border-tint">
          <label className="block text-sm font-semibold text-navy mb-1">Ou introduzir signing secret manualmente</label>
          <p className="text-xs text-muted2 mb-2">Se ja tem o secret gerado do lado da Privaxia, cole-o aqui e guarde nas credenciais acima.</p>
          <input type="password" value={manualSecret} onChange={(e) => setManualSecret(e.target.value)} data-testid="pv-manual-secret" className="w-full px-3 py-2 border border-tint rounded-lg font-mono" placeholder="Cole o signing secret" />
          <div className="text-xs text-muted2 mt-1">Nota: sera guardado ao clicar em "Guardar credenciais".</div>
        </div>
      </div>
    </div>
  );
}


function ChangeEmailModal({ state, platform, onClose, onSaved, onSync }) {
  const [email, setEmail] = useState("");
  const [saving, setSaving] = useState(false);
  const isAdmin = state.kind === "admin";
  const labelPapel = isAdmin ? "administrador" : "gestor de triagem";
  const endpoint = isAdmin
    ? `/${platform}/tenants/${state.tenant.slug}/change-admin-email`
    : `/${platform}/tenants/${state.tenant.slug}/change-triage-email`;

  const save = async () => {
    const trimmed = (email || "").trim().toLowerCase();
    if (!trimmed.includes("@")) return toast.error("Indique um email valido.");
    if (trimmed === (state.current || "").toLowerCase()) return toast.error("O email e igual ao atual.");
    setSaving(true);
    try {
      await api.post(endpoint, { new_email: trimmed });
      toast.success(`Email do ${labelPapel} alterado`);
      onSaved();
    } catch (e) {
      const status = e.response?.status;
      const detail = formatApiErrorDetail(e.response?.data?.detail);
      if (status === 404 && onSync) {
        toast.error(detail, {
          action: { label: "Sincronizar agora", onClick: () => { onClose(); onSync(); } },
          duration: 8000,
        });
      } else {
        toast.error(detail);
      }
    } finally { setSaving(false); }
  };

  return (
    <div className="fixed inset-0 bg-black/40 flex items-center justify-center z-50 p-4" data-testid={`${platform}-changeemail-modal`}>
      <div className="bg-white rounded-xl p-6 w-full max-w-md">
        <div className="flex justify-between items-center mb-5">
          <div>
            <div className="text-xs uppercase tracking-[0.15em] text-blue font-semibold">Utilizadores da plataforma</div>
            <h2 className="text-navy text-xl font-bold tracking-tight">Alterar email do {labelPapel}</h2>
          </div>
          <button onClick={onClose} className="text-muted2 hover:text-navy">x</button>
        </div>
        <div className="mb-3 text-sm bg-tint rounded-lg p-3">
          Tenant: <b className="text-navy">{state.tenant.name || state.tenant.slug}</b><br />
          Slug: <code className="text-xs">{state.tenant.slug}</code>
          {state.current && (<><br />Email atual: <span className="font-mono text-xs">{state.current}</span></>)}
        </div>
        <label className="block text-xs uppercase tracking-wide text-muted2 font-semibold mb-1">Novo email</label>
        <input value={email} onChange={(e) => setEmail(e.target.value)} type="email" autoFocus
          data-testid={`${platform}-changeemail-input`}
          placeholder="novo.email@empresa.pt"
          className="w-full mb-2 px-3 py-2 border border-slate-300 rounded-lg focus:ring-2 focus:ring-blue/20 focus:outline-none" />
        <p className="text-[11px] text-muted2 mb-4">
          O upstream vai enviar-lhe uma nova password se necessario. O SaaS-Hub apenas propaga a alteracao; a plataforma-alvo mantem a identidade e historico do utilizador.
        </p>
        <div className="flex gap-2 justify-end">
          <button onClick={onClose} className="text-sm px-3 py-2 rounded-lg text-navy hover:bg-tint">Cancelar</button>
          <button onClick={save} disabled={saving} data-testid={`${platform}-changeemail-save`}
            className="text-sm px-3 py-2 rounded-lg bg-blue text-white font-semibold hover:bg-navy disabled:opacity-60">
            {saving ? "A alterar..." : "Confirmar alteracao"}
          </button>
        </div>
      </div>
    </div>
  );
}

export { ChangeEmailModal };


function PasswordActionsModal({ state, platform, onClose, onSaved, onSync }) {
  // state: { tenant, role: "admin"|"triage_manager", currentEmail? }
  const [mode, setMode] = useState("reset"); // reset | direct
  const [email, setEmail] = useState("");
  const [pwd, setPwd] = useState("");
  const [pwd2, setPwd2] = useState("");
  const [saving, setSaving] = useState(false);
  const roleLabel = state.role === "admin" ? "administrador" : "gestor de triagem";
  const endpointBase = `/${platform}/tenants/${state.tenant.slug}`;

  const doReset = async () => {
    setSaving(true);
    try {
      const body = { role: state.role };
      const em = (email || "").trim().toLowerCase();
      if (em) body.email = em;
      const res = await api.post(`${endpointBase}/send-password-reset`, body);
      toast.success(res.data?.message || "Email de reset enviado", {
        description: res.data?.to ? `Para: ${res.data.to}` : undefined,
      });
      onSaved();
    } catch (e) {
      const status = e.response?.status;
      const detail = formatApiErrorDetail(e.response?.data?.detail);
      if (status === 404 && onSync) {
        toast.error(detail, {
          action: { label: "Sincronizar agora", onClick: () => { onClose(); onSync(); } },
          duration: 10000,
        });
      } else {
        toast.error(detail);
      }
    } finally { setSaving(false); }
  };

  const doDirect = async () => {
    if ((pwd || "").length < 8) return toast.error("Password minima: 8 caracteres.");
    if (pwd !== pwd2) return toast.error("As passwords nao coincidem.");
    setSaving(true);
    try {
      const body = { role: state.role, new_password: pwd };
      const em = (email || "").trim().toLowerCase();
      if (em) body.email = em;
      const res = await api.post(`${endpointBase}/change-password`, body);
      toast.success(res.data?.message || "Password alterada", {
        description: res.data?.to ? `Para: ${res.data.to}` : undefined,
      });
      onSaved();
    } catch (e) {
      const status = e.response?.status;
      const detail = formatApiErrorDetail(e.response?.data?.detail);
      if (status === 404 && onSync) {
        toast.error(detail, {
          action: { label: "Sincronizar agora", onClick: () => { onClose(); onSync(); } },
          duration: 10000,
        });
      } else {
        toast.error(detail);
      }
    } finally { setSaving(false); }
  };

  return (
    <div className="fixed inset-0 bg-black/40 flex items-center justify-center z-50 p-4"
      data-testid={`${platform}-pwd-modal`}>
      <div className="bg-white rounded-xl p-6 w-full max-w-md">
        <div className="flex justify-between items-center mb-5">
          <div>
            <div className="text-xs uppercase tracking-[0.15em] text-blue font-semibold">Gestao de password</div>
            <h2 className="text-navy text-xl font-bold tracking-tight">Password do {roleLabel}</h2>
          </div>
          <button onClick={onClose} className="text-muted2 hover:text-navy">x</button>
        </div>
        <div className="mb-3 text-sm bg-tint rounded-lg p-3">
          Tenant: <b className="text-navy">{state.tenant.name || state.tenant.slug}</b><br />
          Slug: <code className="text-xs">{state.tenant.slug}</code>
          {state.currentEmail && (<><br />Email atual: <span className="font-mono text-xs">{state.currentEmail}</span></>)}
        </div>
        <div className="flex gap-1 mb-4 border-b border-slate-200">
          <button onClick={() => setMode("reset")}
            data-testid={`${platform}-pwd-mode-reset`}
            className={`px-3 py-2 text-sm font-semibold border-b-2 ${mode === "reset" ? "border-blue text-navy" : "border-transparent text-muted2"}`}>
            Enviar reset por email
          </button>
          <button onClick={() => setMode("direct")}
            data-testid={`${platform}-pwd-mode-direct`}
            className={`px-3 py-2 text-sm font-semibold border-b-2 ${mode === "direct" ? "border-blue text-navy" : "border-transparent text-muted2"}`}>
            Alterar directamente
          </button>
        </div>
        <label className="block text-xs uppercase tracking-wide text-muted2 font-semibold mb-1">
          Email do utilizador <span className="normal-case text-[10px] text-muted2">(opcional - deixe vazio para usar o {roleLabel})</span>
        </label>
        <input value={email} onChange={(e) => setEmail(e.target.value)} type="email"
          data-testid={`${platform}-pwd-email-input`}
          placeholder={state.currentEmail || `${roleLabel}@empresa.pt`}
          className="w-full mb-3 px-3 py-2 border border-slate-300 rounded-lg focus:ring-2 focus:ring-blue/20 focus:outline-none" />
        {mode === "reset" ? (
          <p className="text-[11px] text-muted2 mb-4">
            A plataforma envia um email com link de reset (validade 60 min). O utilizador escolhe a nova password.
          </p>
        ) : (
          <>
            <label className="block text-xs uppercase tracking-wide text-muted2 font-semibold mb-1">Nova password</label>
            <input value={pwd} onChange={(e) => setPwd(e.target.value)} type="password" autoComplete="new-password"
              data-testid={`${platform}-pwd-new-input`}
              placeholder="minimo 8 caracteres"
              className="w-full mb-2 px-3 py-2 border border-slate-300 rounded-lg focus:ring-2 focus:ring-blue/20 focus:outline-none" />
            <label className="block text-xs uppercase tracking-wide text-muted2 font-semibold mb-1">Confirmar password</label>
            <input value={pwd2} onChange={(e) => setPwd2(e.target.value)} type="password" autoComplete="new-password"
              data-testid={`${platform}-pwd-confirm-input`}
              className="w-full mb-2 px-3 py-2 border border-slate-300 rounded-lg focus:ring-2 focus:ring-blue/20 focus:outline-none" />
            <p className="text-[11px] text-amber-700 bg-amber-50 rounded p-2 mb-4">
              Use so em emergencia (cliente sem acesso ao email). O utilizador deve trocar a password no proximo login.
            </p>
          </>
        )}
        <div className="flex gap-2 justify-end">
          <button onClick={onClose} className="text-sm px-3 py-2 rounded-lg text-navy hover:bg-tint">Cancelar</button>
          {mode === "reset" ? (
            <button onClick={doReset} disabled={saving} data-testid={`${platform}-pwd-send-reset`}
              className="text-sm px-3 py-2 rounded-lg bg-blue text-white font-semibold hover:bg-navy disabled:opacity-60">
              {saving ? "A enviar..." : "Enviar reset por email"}
            </button>
          ) : (
            <button onClick={doDirect} disabled={saving} data-testid={`${platform}-pwd-change-direct`}
              className="text-sm px-3 py-2 rounded-lg bg-red-600 text-white font-semibold hover:bg-red-700 disabled:opacity-60">
              {saving ? "A alterar..." : "Alterar password"}
            </button>
          )}
        </div>
      </div>
    </div>
  );
}

export { PasswordActionsModal };


function _fmtActionLabel(a) {
  const map = {
    pv_change_admin_email: "Alteracao email admin",
    pv_send_password_reset: "Envio reset password admin",
    pv_change_password: "Alteracao directa password admin",
    ce_change_admin_email: "Alteracao email admin",
    ce_change_triage_email: "Alteracao email triagem",
    ce_send_password_reset: "Envio reset password",
    ce_change_password: "Alteracao directa password",
    gen_change_admin_email: "Alteracao email admin",
    gen_change_triage_email: "Alteracao email triagem",
    gen_send_password_reset: "Envio reset password",
    gen_change_password: "Alteracao directa password",
  };
  return map[a] || a;
}

function _actionColor(a) {
  if (a.includes("send_password_reset")) return "bg-amber-100 text-amber-800";
  if (a.includes("change_password")) return "bg-red-100 text-red-800";
  if (a.includes("change_admin_email")) return "bg-blue-100 text-blue-800";
  if (a.includes("change_triage_email")) return "bg-purple-100 text-purple-800";
  return "bg-slate-100 text-slate-800";
}

function TenantHistoryModal({ tenant, entidade, onClose }) {
  const [rows, setRows] = useState(null);
  useEffect(() => {
    (async () => {
      try {
        const res = await api.get(`/audit/tenant-changes`, {
          params: { entidade, entidade_id: tenant.slug },
        });
        setRows(res.data || []);
      } catch (e) {
        toast.error(formatApiErrorDetail(e.response?.data?.detail));
        setRows([]);
      }
    })();
  }, [tenant.slug, entidade]);

  const fmtDt = (iso) => { try { return new Date(iso).toLocaleString("pt-PT"); } catch { return iso; } };

  return (
    <div className="fixed inset-0 bg-black/40 flex items-center justify-center z-50 p-4"
      data-testid="tenant-history-modal">
      <div className="bg-white rounded-xl p-6 w-full max-w-3xl max-h-[85vh] flex flex-col">
        <div className="flex justify-between items-start mb-4">
          <div>
            <div className="text-xs uppercase tracking-[0.15em] text-blue font-semibold">Auditoria</div>
            <h2 className="text-navy text-xl font-bold tracking-tight">Alteracoes de utilizadores</h2>
            <div className="text-sm text-muted2 mt-1">
              Tenant: <b className="text-navy">{tenant.name || tenant.slug}</b>
              {" - "}<code className="text-xs bg-tint px-1 rounded">{tenant.slug}</code>
            </div>
          </div>
          <button onClick={onClose} className="text-muted2 hover:text-navy text-xl leading-none">x</button>
        </div>

        <div className="flex-1 overflow-auto -mx-6 px-6">
          {rows === null && <div className="text-sm text-muted2 py-8 text-center">A carregar...</div>}
          {rows && rows.length === 0 && (
            <div className="text-sm text-muted2 py-8 text-center bg-tint rounded-lg">
              Nao existem alteracoes de utilizadores registadas para este tenant.
            </div>
          )}
          {rows && rows.length > 0 && (
            <table className="w-full text-sm">
              <thead>
                <tr className="text-left text-xs uppercase tracking-wide text-muted2 border-b border-slate-200">
                  <th className="py-2 pr-3">Quando</th>
                  <th className="py-2 pr-3">Accao</th>
                  <th className="py-2 pr-3">Ator</th>
                  <th className="py-2">Detalhes</th>
                </tr>
              </thead>
              <tbody data-testid="tenant-history-rows">
                {rows.map((r) => (
                  <tr key={r.id} className="border-b border-slate-100 hover:bg-tint/50">
                    <td className="py-2 pr-3 whitespace-nowrap text-xs">{fmtDt(r.em)}</td>
                    <td className="py-2 pr-3">
                      <span className={`text-[11px] px-2 py-0.5 rounded font-semibold ${_actionColor(r.acao)}`}>
                        {_fmtActionLabel(r.acao)}
                      </span>
                    </td>
                    <td className="py-2 pr-3 text-xs">{r.ator_email || "-"}</td>
                    <td className="py-2 text-xs text-muted2">
                      {r.depois_json?.new_email && <>novo email: <code className="bg-tint px-1 rounded">{r.depois_json.new_email}</code></>}
                      {r.depois_json?.role && !r.depois_json?.new_email && <>role: <b>{r.depois_json.role}</b>{r.depois_json?.email ? <> - email: <code className="bg-tint px-1 rounded">{r.depois_json.email}</code></> : null}</>}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </div>

        <div className="flex justify-end mt-4 border-t border-slate-100 pt-3">
          <button onClick={onClose} className="text-sm px-3 py-2 rounded-lg bg-navy text-white hover:bg-blue">Fechar</button>
        </div>
      </div>
    </div>
  );
}

export { TenantHistoryModal };


function LocalMetadataModal({ tenant, platform, onClose, onSaved }) {
  const isPrivaxia = platform === "privaxia";
  const [triageMode, setTriageMode] = useState(tenant.triage_mode || "");
  const [triageEmail, setTriageEmail] = useState(tenant.triage_email || "");
  const [dpoMode, setDpoMode] = useState(tenant.dpo_mode || "");
  const [saving, setSaving] = useState(false);
  const save = async () => {
    setSaving(true);
    try {
      const body = {};
      if (isPrivaxia) {
        if (dpoMode !== (tenant.dpo_mode || "")) body.dpo_mode = dpoMode;
      } else {
        if (triageMode !== (tenant.triage_mode || "")) body.triage_mode = triageMode;
        if (triageEmail !== (tenant.triage_email || "")) body.triage_email = triageEmail;
      }
      if (!Object.keys(body).length) { toast.info("Sem alteracoes."); setSaving(false); return; }
      await api.patch(`/${platform}/tenants/${tenant.slug}/local-metadata`, body);
      toast.success("Metadata local actualizada");
      onSaved();
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail));
    } finally { setSaving(false); }
  };
  return (
    <div className="fixed inset-0 bg-black/40 flex items-center justify-center z-50 p-4"
      data-testid={`${platform}-local-meta-modal`}>
      <div className="bg-white rounded-xl p-6 w-full max-w-md">
        <div className="flex justify-between items-start mb-4">
          <div>
            <div className="text-xs uppercase tracking-[0.15em] text-blue font-semibold">Metadata local (suporte)</div>
            <h2 className="text-navy text-xl font-bold tracking-tight">Ajustar metadata local</h2>
            <p className="text-[11px] text-muted2 mt-1">Corrige o que o upstream nao expoe. So actualiza a DB local, nao chama upstream.</p>
          </div>
          <button onClick={onClose} className="text-muted2 hover:text-navy text-xl leading-none">x</button>
        </div>
        <div className="text-sm bg-tint rounded-lg p-3 mb-4">
          Tenant: <b className="text-navy">{tenant.name || tenant.slug}</b> - <code className="text-xs">{tenant.slug}</code>
        </div>
        {isPrivaxia ? (
          <>
            <label className="block text-xs uppercase tracking-wide text-muted2 font-semibold mb-1">dpo_mode</label>
            <select value={dpoMode} onChange={(e) => setDpoMode(e.target.value)}
              data-testid={`${platform}-local-meta-dpo-mode`}
              className="w-full mb-3 px-3 py-2 border border-slate-300 rounded-lg focus:ring-2 focus:ring-blue/20 focus:outline-none">
              <option value="">(nao definido)</option>
              <option value="cogniseg">cogniseg (Cogniseg presta o DPO)</option>
              <option value="client">client (cliente tem DPO proprio)</option>
            </select>
          </>
        ) : (
          <>
            <label className="block text-xs uppercase tracking-wide text-muted2 font-semibold mb-1">triage_mode</label>
            <select value={triageMode} onChange={(e) => setTriageMode(e.target.value)}
              data-testid={`${platform}-local-meta-triage-mode`}
              className="w-full mb-3 px-3 py-2 border border-slate-300 rounded-lg focus:ring-2 focus:ring-blue/20 focus:outline-none">
              <option value="">(nao definido)</option>
              <option value="cogniseg">cogniseg (triagem interna Cogniseg)</option>
              <option value="client">client (gestor de triagem no cliente)</option>
            </select>
            <label className="block text-xs uppercase tracking-wide text-muted2 font-semibold mb-1">triage_email</label>
            <input value={triageEmail} onChange={(e) => setTriageEmail(e.target.value)} type="email"
              data-testid={`${platform}-local-meta-triage-email`}
              placeholder="triagem@cliente.pt"
              className="w-full mb-4 px-3 py-2 border border-slate-300 rounded-lg focus:ring-2 focus:ring-blue/20 focus:outline-none" />
          </>
        )}
        <div className="flex gap-2 justify-end">
          <button onClick={onClose} className="text-sm px-3 py-2 rounded-lg text-navy hover:bg-tint">Cancelar</button>
          <button onClick={save} disabled={saving} data-testid={`${platform}-local-meta-save`}
            className="text-sm px-3 py-2 rounded-lg bg-blue text-white font-semibold hover:bg-navy disabled:opacity-60">
            {saving ? "A guardar..." : "Guardar metadata"}
          </button>
        </div>
      </div>
    </div>
  );
}

export { LocalMetadataModal };


function TenantDetailsModal({ tenant, entidade, platform, onClose, onAction }) {
  // onAction: (kind: 'change-email-admin'|'change-email-triage'|'password-admin'|'password-triage'|'local-metadata') => void
  const { can } = useAuth();
  const isSuperAdmin = can("*");
  const [history, setHistory] = useState([]);
  const [planData, setPlanData] = useState(null); // { subscription, plan }
  const [demoLoading, setDemoLoading] = useState(false);
  useEffect(() => {
    api.get(`/audit/tenant-changes`, {
      params: { entidade, entidade_id: tenant.slug },
    }).then(({ data }) => setHistory(data || [])).catch(() => setHistory([]));
    // Carregar subscricao e plano do catalogo SaaS-Hub
    api.get(`/subscriptions/by-tenant`, { params: { platform, slug: tenant.slug } })
      .then(({ data }) => setPlanData(data))
      .catch(() => setPlanData(null));
  }, [tenant.slug, entidade, platform]);

  const toggleDemo = async () => {
    const sub = planData?.subscription;
    if (!sub?.id) return toast.error("Sem subscricao associada");
    setDemoLoading(true);
    try {
      const newVal = !sub.is_demo;
      await api.patch(`/subscriptions/${sub.id}/demo`, { is_demo: newVal });
      toast.success(newVal ? "Tenant marcado como Demo" : "Tenant removido do modo Demo");
      // Refresh plan data
      const { data } = await api.get(`/subscriptions/by-tenant`, { params: { platform, slug: tenant.slug } });
      setPlanData(data);
    } catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail)); }
    finally { setDemoLoading(false); }
  };

  const lastByAction = (predicate) => {
    return history.find((h) => predicate(h.acao || ""));
  };
  const lastPwdAdmin = lastByAction((a) => a.endsWith("send_password_reset") || a.endsWith("change_password"));
  const lastPwdTriage = null; // requires role info - keep generic
  const fmtDt = (iso) => { try { return new Date(iso).toLocaleString("pt-PT"); } catch { return iso; } };

  const isCogniEtica = platform === "cognietica";
  const showTriage = isCogniEtica && (tenant.triage_mode === "client" || tenant.triage_email);

  return (
    <div className="fixed inset-0 bg-black/40 flex items-center justify-center z-50 p-4" data-testid={`${platform}-details-modal`}>
      <div className="bg-white rounded-xl p-6 w-full max-w-2xl max-h-[90vh] flex flex-col">
        <div className="flex justify-between items-start mb-4">
          <div>
            <div className="text-xs uppercase tracking-[0.15em] text-blue font-semibold">Ficha consolidada do tenant</div>
            <h2 className="text-navy text-xl font-bold tracking-tight">{tenant.name || tenant.slug}</h2>
            <div className="text-sm text-muted2 mt-1">
              Slug: <code className="text-xs bg-tint px-1 rounded">{tenant.slug}</code>
              {tenant.plan && <> - Plano: <b className="text-navy">{tenant.plan}</b></>}
              {typeof tenant.is_active !== "undefined" && (
                <span className={`ml-2 text-[10px] px-1.5 py-0.5 rounded ${tenant.is_active ? "bg-green-50 text-green-700" : "bg-red-50 text-red-700"}`}>
                  {tenant.is_active ? "activa" : "suspensa"}
                </span>
              )}
              {planData?.subscription?.is_demo && (
                <span className="ml-2 text-[10px] px-1.5 py-0.5 rounded bg-purple-100 text-purple-700 font-semibold" data-testid={`${platform}-demo-badge`}>Demo</span>
              )}
            </div>
          </div>
          <button onClick={onClose} className="text-muted2 hover:text-navy text-xl leading-none">x</button>
        </div>

        <div className="flex-1 overflow-auto -mx-6 px-6 space-y-4">
          {/* Demo toggle - so super_admin */}
          {isSuperAdmin && planData?.subscription?.id && (
            <section className="border border-purple-200 rounded-lg p-3 bg-purple-50/40" data-testid={`${platform}-demo-toggle-section`}>
              <div className="flex items-center justify-between">
                <div>
                  <div className="text-[10px] uppercase tracking-wider text-purple-700 font-semibold">Modo Demo</div>
                  <div className="text-xs text-purple-600 mt-0.5">Tenants Demo nao contam para MRR/ARR no Dashboard.</div>
                </div>
                <label className="relative inline-flex items-center cursor-pointer">
                  <input
                    type="checkbox"
                    checked={!!planData?.subscription?.is_demo}
                    onChange={toggleDemo}
                    disabled={demoLoading}
                    data-testid={`${platform}-demo-toggle`}
                    className="sr-only peer"
                  />
                  <div className="w-9 h-5 bg-slate-200 peer-focus:outline-none rounded-full peer peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:border-gray-300 after:border after:rounded-full after:h-4 after:w-4 after:transition-all peer-checked:bg-purple-600"></div>
                  <span className="ml-2 text-xs font-semibold text-purple-700">{planData?.subscription?.is_demo ? "Ativo" : "Inativo"}</span>
                </label>
              </div>
            </section>
          )}

          <section className="border border-tint rounded-lg p-4" data-testid={`${platform}-details-admin`}>
            <div className="flex items-start justify-between gap-3">
              <div>
                <div className="text-[10px] uppercase tracking-wider text-blue font-semibold">Administrador</div>
                <div className="text-navy font-bold">{tenant.admin_email || <span className="text-muted2 italic">Nao disponivel</span>}</div>
                {lastPwdAdmin && (
                  <div className="text-xs text-muted2 mt-1">
                    Ultima acao de password: <b className="text-navy">{fmtDt(lastPwdAdmin.em)}</b> por {lastPwdAdmin.ator_email || "-"}
                  </div>
                )}
              </div>
              <div className="flex gap-1 flex-wrap justify-end shrink-0">
                <button onClick={() => onAction("change-email-admin")}
                  data-testid={`${platform}-details-admin-email-btn`}
                  className="text-xs px-2 py-1 rounded bg-tint text-navy hover:bg-blue hover:text-white">Alterar email</button>
                <button onClick={() => onAction("password-admin")}
                  data-testid={`${platform}-details-admin-pwd-btn`}
                  className="text-xs px-2 py-1 rounded bg-tint text-navy hover:bg-blue hover:text-white">Password</button>
              </div>
            </div>
          </section>

          {planData && (planData.subscription || planData.plan) && (
            <TenantPlanOverrideSection planData={planData} platform={platform} tenant={tenant} onSaved={() => {
              api.get(`/subscriptions/by-tenant`, { params: { platform, slug: tenant.slug } })
                .then(({ data }) => setPlanData(data)).catch(() => {});
            }} />
          )}

          {showTriage && (
            <section className="border border-tint rounded-lg p-4" data-testid={`${platform}-details-triage`}>
              <div className="flex items-start justify-between gap-3">
                <div>
                  <div className="text-[10px] uppercase tracking-wider text-blue font-semibold">Gestor de triagem</div>
                  <div className="text-navy font-bold">{tenant.triage_email || <span className="text-muted2 italic">Sem email registado localmente</span>}</div>
                  <div className="text-[11px] text-muted2">
                    triage_mode: <code className="bg-tint px-1 rounded">{tenant.triage_mode || "(nao definido)"}</code>
                  </div>
                </div>
                <div className="flex gap-1 flex-wrap justify-end shrink-0">
                  <button onClick={() => onAction("change-email-triage")}
                    data-testid={`${platform}-details-triage-email-btn`}
                    className="text-xs px-2 py-1 rounded bg-tint text-navy hover:bg-blue hover:text-white">Alterar email</button>
                  <button onClick={() => onAction("password-triage")}
                    data-testid={`${platform}-details-triage-pwd-btn`}
                    className="text-xs px-2 py-1 rounded bg-tint text-navy hover:bg-blue hover:text-white">Password / reset</button>
                </div>
              </div>
            </section>
          )}

          <section className="border border-tint rounded-lg p-4" data-testid={`${platform}-details-audit`}>
            <div className="text-[10px] uppercase tracking-wider text-blue font-semibold mb-2">Ultimas alteracoes (top 5)</div>
            {history.length === 0 && <div className="text-xs text-muted2 italic">Sem registos.</div>}
            {history.slice(0, 5).map((h) => (
              <div key={h.id} className="text-xs py-1 border-b border-slate-50 last:border-b-0 flex items-center gap-2">
                <span className="text-slate-500 whitespace-nowrap">{fmtDt(h.em)}</span>
                <span className="font-semibold text-navy">{(h.acao || "").replace(/^..?_/, "")}</span>
                <span className="text-muted2 truncate">
                  {h.depois_json?.new_email && `-> ${h.depois_json.new_email}`}
                  {h.depois_json?.role && !h.depois_json?.new_email && `role ${h.depois_json.role}`}
                </span>
              </div>
            ))}
          </section>
        </div>

        <div className="mt-4 pt-3 border-t border-slate-100 flex justify-between items-center">
          <button onClick={() => onAction("local-metadata")}
            data-testid={`${platform}-details-local-meta-btn`}
            className="text-xs px-3 py-1.5 rounded bg-amber-50 text-amber-700 border border-amber-200 hover:bg-amber-100 font-semibold">
            Ajustar metadata local
          </button>
          <button onClick={onClose} className="text-sm px-3 py-2 rounded-lg bg-navy text-white hover:bg-blue">Fechar</button>
        </div>
      </div>
    </div>
  );
}

export { TenantDetailsModal };




const MENSAL_MAP_O = { anual: "preco_mensal_1ano", "3anos": "preco_mensal_3anos", "5anos": "preco_mensal_5anos" };
const PER_LABEL_O = { anual: "Anual", "3anos": "3 anos", "5anos": "5 anos" };

function TenantPlanOverrideSection({ planData, platform, tenant, onSaved }) {
  const p = planData.plan || {};
  const sub = planData.subscription || {};
  const hasPlan = !!planData.plan;
  const isCE = platform === "cognietica";
  const periodo = sub.periodo || "anual";
  const mensalKey = MENSAL_MAP_O[periodo] || "preco_mensal_1ano";
  const planMrr = p[mensalKey] || 0;
  const mrrBase = sub.mrr || planMrr || 0;
  const addonVal = isCE ? (p.addon_triagem_mensal || 0) : (p.addon_dpo_mensal || 0);
  const addonLabel = isCE ? "Gestao Triagem" : "DPO as a Service";
  const serviceActive = isCE
    ? (tenant.triage_mode || "").toLowerCase() === "cogniseg"
    : (tenant.dpo_mode || "").toLowerCase() === "cogniseg";

  // Override state
  const [ovMrr, setOvMrr] = useState(sub.override_mrr ?? "");
  const [ovDen, setOvDen] = useState(sub.override_max_denuncias ?? "");
  const [ovExDen, setOvExDen] = useState(sub.override_valor_extra_denuncia ?? "");
  const [ovTit, setOvTit] = useState(sub.override_max_titulares ?? "");
  const [saving, setSaving] = useState(false);
  const [dirty, setDirty] = useState(false);

  const mark = (setter) => (v) => { setter(v); setDirty(true); };

  const effectiveMrr = ovMrr !== "" && ovMrr !== null ? Number(ovMrr) : mrrBase;
  const effectiveAddon = serviceActive && addonVal > 0 ? addonVal : 0;
  const mrrTotal = effectiveMrr + effectiveAddon;

  const effectiveDen = ovDen !== "" && ovDen !== null ? Number(ovDen) : (p.max_denuncias ?? -1);
  const effectiveExDen = ovExDen !== "" && ovExDen !== null ? Number(ovExDen) : (p.valor_extra_denuncia ?? 0);
  const effectiveTit = ovTit !== "" && ovTit !== null ? Number(ovTit) : (p.max_titulares ?? -1);

  const saveOverrides = async () => {
    if (!sub.id) return toast.error("Subscricao nao encontrada.");
    setSaving(true);
    try {
      const body = { override_mrr: ovMrr !== "" ? Number(ovMrr) : null };
      if (isCE) {
        body.override_max_denuncias = ovDen !== "" ? Number(ovDen) : null;
        body.override_valor_extra_denuncia = ovExDen !== "" ? Number(ovExDen) : null;
      } else {
        body.override_max_titulares = ovTit !== "" ? Number(ovTit) : null;
      }
      await api.patch(`/subscriptions/${sub.id}/overrides`, body);
      toast.success("Valores negociados guardados");
      setDirty(false);
      onSaved();
    } catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail)); }
    finally { setSaving(false); }
  };

  const hasOverrides = ovMrr !== "" || ovDen !== "" || ovExDen !== "" || ovTit !== "";

  return (
    <section className="border border-blue/20 rounded-lg p-4 bg-gradient-to-r from-slate-50 to-white" data-testid={`${platform}-details-plan`}>
      <div className="flex items-center justify-between mb-3">
        <div className="text-[10px] uppercase tracking-wider text-blue font-semibold">
          {hasPlan ? "Plano contratado (SaaS-Hub)" : "Subscricao ativa"}
        </div>
        {hasOverrides && (
          <span className="text-[10px] bg-amber-100 text-amber-800 px-2 py-0.5 rounded-full font-semibold" data-testid={`${platform}-negociado-badge`}>
            Valores negociados
          </span>
        )}
      </div>

      {/* Info plano */}
      <div className="grid grid-cols-2 gap-x-6 gap-y-2 text-sm mb-3">
        <div>
          <span className="text-muted2 text-xs">Plano</span>
          <div className="text-navy font-bold">{hasPlan ? p.nome : (sub.plano_nome || "-")}</div>
        </div>
        {hasPlan && (
          <div>
            <span className="text-muted2 text-xs">SKU</span>
            <div><code className="text-xs bg-tint px-2 py-0.5 rounded">{p.sku}</code></div>
          </div>
        )}
        <div>
          <span className="text-muted2 text-xs">Periodo</span>
          <div className="text-navy font-semibold">{PER_LABEL_O[periodo] || periodo}</div>
        </div>
        {hasPlan && p.max_utilizadores > 0 && (
          <div>
            <span className="text-muted2 text-xs">Max utilizadores</span>
            <div className="text-navy font-bold">{p.max_utilizadores}</div>
            <div className="text-[10px] text-muted2">User Cogniseg (triagem/DPO) nao conta</div>
          </div>
        )}
      </div>

      {/* Override MRR */}
      <div className="border border-tint rounded-lg p-3 mb-3 bg-white">
        <div className="text-[10px] uppercase tracking-wider text-navy font-semibold mb-2">Preco mensal (MRR)</div>
        <div className="grid grid-cols-2 gap-3 items-end">
          <div>
            <span className="text-muted2 text-[10px]">Valor do plano</span>
            <div className="text-navy text-sm">{eur(planMrr)}/mes</div>
          </div>
          <div>
            <span className="text-amber-700 text-[10px] font-semibold">Override negociado</span>
            <input type="number" value={ovMrr} onChange={(e) => mark(setOvMrr)(e.target.value)}
              placeholder={String(planMrr || mrrBase)} data-testid={`${platform}-override-mrr`}
              className="w-full px-2 py-1.5 border border-amber-200 rounded text-sm bg-amber-50/50 focus:ring-amber-300 focus:border-amber-400 focus:outline-none" step="0.01" />
          </div>
        </div>
        {addonVal > 0 && (
          <div className="mt-2 text-xs text-muted2">
            + Add-on {addonLabel}: {eur(addonVal)}/mes {serviceActive ? "(ativo)" : "(inativo)"}
          </div>
        )}
      </div>

      {/* Override Limites CE */}
      {isCE && (
        <div className="border border-amber-200 rounded-lg p-3 mb-3 bg-amber-50/30">
          <div className="text-[10px] uppercase tracking-wider text-amber-800 font-semibold mb-2">Limites denuncias</div>
          <div className="grid grid-cols-2 gap-3">
            <div>
              <div className="flex items-center justify-between mb-1">
                <span className="text-muted2 text-[10px]">Plano: {p.max_denuncias === -1 ? "Ilimitado" : p.max_denuncias}</span>
              </div>
              <label className="text-amber-700 text-[10px] font-semibold">Max denuncias/mes (negociado)</label>
              <input type="number" value={ovDen} onChange={(e) => mark(setOvDen)(e.target.value)}
                placeholder={String(p.max_denuncias ?? -1)} data-testid={`${platform}-override-den`}
                className="w-full px-2 py-1.5 border border-amber-200 rounded text-sm bg-white" />
            </div>
            <div>
              <div className="flex items-center justify-between mb-1">
                <span className="text-muted2 text-[10px]">Plano: {eur(p.valor_extra_denuncia || 0)}/un</span>
              </div>
              <label className="text-amber-700 text-[10px] font-semibold">Valor extra/denuncia (negociado)</label>
              <input type="number" value={ovExDen} onChange={(e) => mark(setOvExDen)(e.target.value)}
                placeholder={String(p.valor_extra_denuncia || 0)} data-testid={`${platform}-override-exden`}
                className="w-full px-2 py-1.5 border border-amber-200 rounded text-sm bg-white" step="0.01" />
            </div>
          </div>
          <div className="mt-2 text-[10px] text-muted2">
            Efetivo: {effectiveDen === -1 ? "Ilimitado" : effectiveDen} denuncias/mes | {eur(effectiveExDen)}/un extra
          </div>
        </div>
      )}

      {/* Override Limites PV */}
      {!isCE && (
        <div className="border border-blue/20 rounded-lg p-3 mb-3 bg-blue/5">
          <div className="text-[10px] uppercase tracking-wider text-blue font-semibold mb-2">Limites titulares</div>
          <div className="grid grid-cols-2 gap-3 items-end">
            <div>
              <span className="text-muted2 text-[10px]">Plano: {p.max_titulares === -1 ? "Ilimitado" : p.max_titulares}</span>
              <div className="text-navy text-sm">{p.max_titulares === -1 ? "Ilimitado" : p.max_titulares} titulares</div>
            </div>
            <div>
              <label className="text-blue text-[10px] font-semibold">Max titulares (negociado)</label>
              <input type="number" value={ovTit} onChange={(e) => mark(setOvTit)(e.target.value)}
                placeholder={String(p.max_titulares ?? -1)} data-testid={`${platform}-override-tit`}
                className="w-full px-2 py-1.5 border border-blue/30 rounded text-sm bg-white" />
            </div>
          </div>
          <div className="mt-2 text-[10px] text-muted2">
            Efetivo: {effectiveTit === -1 ? "Ilimitado" : effectiveTit} titulares
          </div>
        </div>
      )}

      {/* MRR total + ARR */}
      <div className="flex items-center justify-between pt-3 border-t border-tint">
        <div>
          <span className="text-muted2 text-xs">MRR total</span>
          <div className="text-navy font-bold text-lg" data-testid={`${platform}-details-mrr`}>{eur(mrrTotal)}/mes</div>
        </div>
        <div>
          <span className="text-muted2 text-xs">ARR</span>
          <div className="text-navy font-bold text-lg">{eur(mrrTotal * 12)}/ano</div>
        </div>
      </div>

      {/* Save */}
      {sub.id && (
        <div className="mt-3 flex items-center justify-between">
          {!hasPlan && (
            <div className="text-[11px] text-amber-600 bg-amber-50 rounded px-2 py-1">
              Sem plano do catalogo associado.
            </div>
          )}
          <div className="ml-auto">
            <Btn onClick={saveOverrides} disabled={saving || !dirty} testid={`${platform}-override-save`}>
              {saving ? "..." : "Guardar valores negociados"}
            </Btn>
          </div>
        </div>
      )}
    </section>
  );
}
